import fs from 'fs';
import path from 'path';
import prompts from 'prompts';

import { runCommands } from '../../utils/runCommands';

// The order of the keys matters for aesthetic reasons.
// If a field ends up being 'undefined' in the end it'll be removed when
// we stringify it.
const packageJsonDefaults = {
  name: undefined,
  private: true,
  version: '0.0.0',
  description: '',
  author: 'Jay Phelps <hello@jayphelps.com>',
  license: 'MIT',
  homepage: undefined as undefined | string,
  bugs: undefined as
    | undefined
    | {
        url: string;
      },
  repository: undefined as
    | undefined
    | {
        type: 'git';
        url: string;
      },
  main: './dist/cjs/index.js',
  type: 'module',
  exports: {
    import: './dist/esm/index.js',
    require: './dist/cjs/index.js',
  },
  files: ['dist'],
  scripts: {
    start: 'jay-scripts start',
    build: 'jay-scripts build',
    test: 'jay-scripts test',
  },
  devDependencies: {
    '@jayphelps/jay-scripts': 'latest',
  },
};

const license = `MIT License

Copyright (c) ${new Date().getFullYear()}-present Jay Phelps, and contributors

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
`;
const readmeGen = (name: string) => `
# ${name}
[![npm version](https://img.shields.io/npm/v/${name}.svg)](https://www.npmjs.com/package/${name})
[![npm downloads](https://img.shields.io/npm/dm/${name}.svg)](https://www.npmjs.com/package/${name})

## Install

\`\`\`console
npm install --save ${name}
# or
yarn add ${name}

:shipit:
`;
const tsconfig = {
  include: ['src', 'test', 'types'],
  extends: '@jayphelps/jay-scripts/configs/tsconfig.json',
};
const prettierconfig = `module.exports = {
  printWidth: 80,
  tabWidth: 2,
  singleQuote: true,
  trailingComma: 'all',
};
`;
const gitignore = `${['node_modules/', 'dist/'].join('\n')}\n`;
const indexts = 'console.log("hello world!");\n';

async function createProject(name: string) {
  // If an @namespace isn't used this just returns the original unmodified.
  const nameWithoutNamespace = name.replace(/^@\S+\/(\S+)$/, '$1');
  const packageJson = { ...packageJsonDefaults };

  const { version } = await prompts({
    name: 'version',
    type: 'text',
    message: 'version:',
    validate: (value: string) => {
      // Naive cause I don't need it more complex.
      return !!value.match(/^[0-9]\.[0-9]\.[0-9]$/);
    },
    initial: '0.0.0',
  });

  const { description } = await prompts({
    name: 'description',
    type: 'text',
    message: 'description:',
    initial: '',
  });

  const { github } = await prompts({
    name: 'github',
    type: 'text',
    message: 'Github org/username:',
    initial: `jayphelps/${nameWithoutNamespace}`,
  });

  const repository = {
    type: 'git',
    url: `git+https://github.com/${github}.git`,
  };
  const bugs = {
    url: `https://github.com/${github}/issues`,
  };
  const homepage = `https://github.com/${github}/`;

  // This is done this way instead of { ...spreading } so that the original
  // key order is still maintained when  it's stringified to JSON.
  Object.assign(packageJson, {
    name,
    version,
    description,
    repository,
    bugs,
    homepage,
  });

  const projectDirPath = path.resolve(nameWithoutNamespace);

  // TODO: prolly should just bite the bullet and use Yeoman...

  /* /project */
  fs.mkdirSync(projectDirPath);
  fs.writeFileSync(
    path.join(projectDirPath, 'package.json'),
    `${JSON.stringify(packageJson, null, 2)}\n`,
  );
  fs.writeFileSync(
    path.join(projectDirPath, 'tsconfig.json'),
    `${JSON.stringify(tsconfig, null, 2)}\n`,
  );
  fs.writeFileSync(
    path.join(projectDirPath, 'prettier.config.js'),
    prettierconfig,
  );
  fs.writeFileSync(path.join(projectDirPath, '.gitignore'), gitignore);
  fs.writeFileSync(path.join(projectDirPath, 'README.md'), readmeGen(name));
  fs.writeFileSync(path.join(projectDirPath, 'LICENSE'), license);

  /* /project/src */
  fs.mkdirSync(path.join(projectDirPath, 'src'));
  fs.writeFileSync(path.join(projectDirPath, 'src', 'index.ts'), indexts);

  /* /project/types */
  fs.mkdirSync(path.join(projectDirPath, 'types'));

  const finalizeCmd = [
    `cd "${projectDirPath}"`,
    'yarn',
    'yarn build',
    'node .',
    `code .`,
  ].join(' && ');

  await runCommands([finalizeCmd]);

  console.log(`Created project '${name}' in ${nameWithoutNamespace}/`);
}

export default async function create(name: string): Promise<void> {
  try {
    await createProject(name);
  } catch (e) {
    console.error(e);
    console.error('Creating a new project failed.');
    process.exit(1);
  }
}
